import { QueryClient, QueryCache, MutationCache } from "@tanstack/react-query";
import { logger } from "./logger";

/**
 * Centralized React Query Client Configuration
 *
 * This configuration provides:
 * - Global error handling for all queries and mutations
 * - Smart retry logic that skips retries for auth errors
 * - Sensible default stale times and garbage collection
 * - Disabled refetch on window focus for better UX
 */
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      const parsedError = error instanceof Error 
        ? error 
        : (error && typeof error === "object" && "message" in error) 
          ? new Error((error as Record<string, unknown>).message as string) 
          : new Error(String(error));
      logger.error(
        "Query failed",
        parsedError,
        { queryKey: query.queryKey }
      );
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      const parsedError = error instanceof Error 
        ? error 
        : (error && typeof error === "object" && "message" in error) 
          ? new Error((error as Record<string, unknown>).message as string) 
          : new Error(String(error));
      logger.error(
        "Mutation failed",
        parsedError,
        { mutationKey: mutation.options.mutationKey }
      );
    },
  }),
  defaultOptions: {
    queries: {
      // Data is considered fresh for 5 minutes
      staleTime: 1000 * 60 * 5,

      // Keep unused data in cache for 30 minutes
      gcTime: 1000 * 60 * 30,

      // Smart retry logic
      retry: (failureCount, error) => {
        // Don't retry on authentication errors
        if (error instanceof Error) {
          if (error.message.includes("401") || error.message.includes("403")) {
            return false;
          }
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },

      // Disable refetch on window focus for better UX
      // Users can manually refresh if needed
      refetchOnWindowFocus: false,
    },
  },
});
